import axios from 'axios';
import { mapUnitType, getUnitType, flattenMatchEntry } from '../composables/useCoachData.js';

// Match statuses that mean "rsparse cannot have written any overviews yet".
// Coach::MatchQuery streams overviews in per round over the ~50s a demo takes,
// and Match#analyze parks the match on queued_for_analysis for that whole
// window before finish_analysis flips it to analyzed. Everything not listed is
// final — analyzed (so rsparse simply never processed this match) or one of the
// ..._failed / expired / demo_not_available dead ends — and will never produce
// rsparse rows.
export const MATCH_IN_PROGRESS_STATUSES = new Set([
  'created',
  'retrieved',
  'parsed',
  'enriched',
  'queued_for_analysis',
]);

/**
 * What the rsparse match page can render from a `/coach/matches/:id` payload.
 *
 * An empty `overviews` list is two states with opposite handling and `status` is
 * the only thing telling them apart: a match opened right after it was played
 * has nothing yet and will fill in over MatchChannel ('processing'), while a
 * finished match with nothing was never processed by rsparse ('unavailable').
 */
export function rsparseMatchState(data) {
  if (data?.overviews?.length) return 'ready';

  return MATCH_IN_PROGRESS_STATUSES.has(data?.status) ? 'processing' : 'unavailable';
}

// Peer-relative stat keys the old Go payload and the rsparse payload disagree
// about. The rsparse side is a ClickHouse column name, so it carries the
// aggregation in the name.
const LEGACY_TO_RSPARSE_STAT = {
  time_to_kill: 'time_to_kill_average',
  time_to_damage: 'time_to_damage_average',
  crosshair_placement: 'crosshair_placement_average',
};

/**
 * The profile key for callers that still look stats up by the legacy name.
 */
export function coachStatKey(legacyKey) {
  return LEGACY_TO_RSPARSE_STAT[legacyKey] || legacyKey;
}

const RSPARSE_TO_LEGACY_STAT = Object.fromEntries(
  Object.entries(LEGACY_TO_RSPARSE_STAT).map(([legacy, rsparse]) => [rsparse, legacy]),
);

/**
 * Display label for an rsparse stat key.
 *
 * The key is a ClickHouse column name, so it carries the aggregation, and the
 * dashboard renders it into a sentence that already says "average":
 * `time_to_kill_average` came out as "your time to kill average average was
 * worse by 12.40%". The map above is the authoritative rename for the keys the
 * two pipelines share; any other `*_average` column just loses the suffix. The
 * raw key stays the identity key — only the label changes.
 */
export function rsparseStatLabel(key) {
  if (!key) return key;

  return RSPARSE_TO_LEGACY_STAT[key] || key.replace(/_average$/, '');
}

/**
 * Time-to-kill in seconds, for the "play this mod" server launch.
 * `time_to_kill_average` is already in seconds.
 */
export function timeToKillSeconds(stats) {
  return stats?.[LEGACY_TO_RSPARSE_STAT.time_to_kill]?.players_value ?? null;
}

/**
 * Reshapes the rsparse profile's `averages` into the stat map the dashboard
 * already renders.
 *
 * The enriched averages Coach::ProfileBuilder returns are already the same shape
 * the legacy /matches/user_profile `stats` were — players_value,
 * rating_range_average_value, deviations_away, unit_type, is_ascending,
 * recommended_mods — with two gaps: unit_type arrives as the backend's symbol
 * (`time`, not `seconds`), and there is no per-stat `name`, which the dashboard
 * renders as a label.
 */
function normalizeRsparseStats(averages) {
  const stats = {};

  Object.entries(averages || {}).forEach(([key, stat]) => {
    stats[key] = {
      ...stat,
      name: stat?.name ?? rsparseStatLabel(key),
      unit_type: stat?.unit_type ? mapUnitType(stat.unit_type) : getUnitType(key),
    };
  });

  return stats;
}

// Anything unparseable stays null rather than becoming a false 0.
function profileMatchCount(value) {
  if (value == null || value === '') return null;

  const count = Number(value);

  return Number.isFinite(count) ? count : null;
}

/**
 * The dashboard's coach profile, as `{ stats, mostPlayedMaps, matchCount }`.
 *
 * `matchCount` is null when the backend reported none — callers have to tell
 * "played nothing" apart from "we don't know", since only the first means the
 * coach should ask for more matches.
 */
export async function fetchCoachProfile(user) {
  const { data } = await axios.get(`/coach/players/${user.steam_id}/profile`);

  return {
    stats: normalizeRsparseStats(data?.averages),
    mostPlayedMaps: data?.extra?.most_played_maps || [],
    matchCount: profileMatchCount(data?.extra?.match_count ?? data?.matches_played),
  };
}

/**
 * Recent per-match stats for the dashboard's trend chart and match lists.
 *
 * `steamId` is whose history to load and defaults to the viewer's own.
 */
export async function fetchCoachMatchHistory(user, params = {}, options = {}) {
  // Passed-but-empty stays empty: falling back to the viewer would show their own
  // matches on someone else's profile.
  const targetSteamId = 'steamId' in options ? options.steamId : user?.steam_id;
  if (!targetSteamId) return { matches: [], totalCount: 0, totalPages: 0 };

  const { data } = await axios.get(`/coach/players/${targetSteamId}/recent_match_stats`, { params });

  return {
    matches: (data?.matches || []).map(flattenMatchEntry),
    totalCount: data?.pagination?.total_count || 0,
    totalPages: data?.pagination?.total_pages || 0,
  };
}
